import { addError } from "@wayflyer/blackbox-fe";
import { StatusBanner, UploadArea, FilesList, Flex, Form, List, Text } from "@wayflyer/flyui";
import type { FundingApplicationSchemas } from "api-types";
import dayjs from "dayjs";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import utc from "dayjs/plugin/utc";
import * as React from "react";
import { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { getEndDateForPeriodText, UPLOAD_HELPER_MESSAGES, useErrorModal } from "shared-ui";

import { useAirBankStatementUpdateMutation } from "../../../queries/air-response/air-bank-statement.mutations";
import { useBankStatementSubmitMutation } from "../../../queries/bank-connect/bank-statement.mutations";
import { COMMON_MESSAGES } from "../../common-messages";
import { ConfirmBankStatementFields } from "../components";
import { useBankStatementUploadManager } from "../hooks/bank-statement-upload-manager";
import { AIR_MESSAGES } from "../message-definitions";
import type { BankStatementUploadsFormFields } from "../types/bank-statements";
import { getAcceptedFileTypes, getStatementTypeMessage, getSupportedFileExtensions } from "../utils";

import { MESSAGES } from "./message-definitions";

dayjs.extend(LocalizedFormat);
dayjs.extend(utc);

export interface BankStatementUploadProps {
  airPublicId: string;
  bankData: FundingApplicationSchemas.BankStatementsForAccountAIRSchema;
  customerDomain: string;
  onBack: () => void;
}

/**
 * BankStatementUploads component
 */
export const BankStatementUpload = ({
  airPublicId,
  bankData,
  customerDomain,
  onBack,
}: BankStatementUploadProps): UIElement => {
  const intl = useIntl();

  const [unableToProvideStatements, setUnableToProvideStatements] = useState<boolean | null>(null);
  const [unableToSendStatmentsReasonsValid, setUnableToSendStatmentsReasonsValid] = useState<boolean>(false);

  const submitBankStatements = useBankStatementSubmitMutation(customerDomain);
  const updateBankStatementsAIR = useAirBankStatementUpdateMutation(customerDomain);

  const { openErrorModal } = useErrorModal();
  const logErrorHandler = React.useCallback(
    (e: unknown) => {
      if (e instanceof Error) {
        openErrorModal({ variant: "submit-error", error: e });
      }
    },
    [openErrorModal]
  );

  const useUploadManager = useBankStatementUploadManager(customerDomain);
  const fileUploadProps = useUploadManager();

  // Generate helperText from upload constraints; extensions match accepted types for this AIR's periods
  const supportedExtensions = getSupportedFileExtensions(bankData.periods);
  const helperText = useMemo(() => {
    let text = "";

    if (fileUploadProps.sizeLimitMB) {
      text += intl.formatMessage(UPLOAD_HELPER_MESSAGES.uploadFileSizeLimit, {
        sizeLimitMb: fileUploadProps.sizeLimitMB,
      });
    }

    if (supportedExtensions.length > 0) {
      text +=
        " " +
        intl.formatMessage(UPLOAD_HELPER_MESSAGES.supportedFileExtensionUpload, {
          supportedFileExtensions: supportedExtensions.join(", "),
        });
    }

    return text.trim();
  }, [fileUploadProps.sizeLimitMB, supportedExtensions, intl]);

  const form = useForm<BankStatementUploadsFormFields>({
    defaultValues: {
      file_uploads: [],
      unable_to_provide_statements_reason: null,
      unable_to_provide_statements_reason_other: null,
      unable_to_provide_statements_reason_open_date: null,
      unable_to_provide_statements_reason_close_dates: null,
    },
  });

  const [formError, setFormError] = useState<string | undefined>();

  React.useEffect(() => {
    form.setValue(
      "file_uploads",
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      fileUploadProps.uploadsState.uploads
        .filter((uploadState) => uploadState.status === "complete" && uploadState.uploadedFile)
        .map((uploadState) => uploadState.uploadedFile)
    );
  }, [fileUploadProps.uploadsState, form]);

  const postAirUpdate = (objectKeys: string[]) => {
    const unableValid = unableToSendStatmentsReasonsValid;
    const closeDates = form.watch("unable_to_provide_statements_reason_close_dates");
    const accountCloseDate = unableValid && closeDates?.[0]?.closeDate ? closeDates[0].closeDate : null;
    updateBankStatementsAIR
      .mutateAsync({
        airPublicId,
        objectKeys,
        unableToProvideReason: unableValid ? form.watch("unable_to_provide_statements_reason") : null,
        unableToProvideOtherText: unableValid ? form.watch("unable_to_provide_statements_reason_other") : null,
        accountOpenDate: unableValid ? form.watch("unable_to_provide_statements_reason_open_date") : null,
        accountCloseDate,
      })
      .then(() => {
        onBack();
      })
      .catch((e) => {
        logErrorHandler(e);
      });
  };

  const submitClickHandler = (formData: BankStatementUploadsFormFields) => {
    const objectKeys = formData.file_uploads.map((file) => file.objectKey);
    if (objectKeys.length) {
      submitBankStatements
        .mutateAsync({
          object_keys: objectKeys,
          group: bankData.bank_name ? bankData.bank_name : `Account ${bankData.bank_account_mask}`,
        })
        .then(() => {
          postAirUpdate(objectKeys);
        })
        .catch((e) => {
          logErrorHandler(e);
        });
    } else if (unableToSendStatmentsReasonsValid) {
      postAirUpdate(objectKeys);
    }
  };

  const onUploadsComplete = (formData: BankStatementUploadsFormFields) => {
    try {
      submitClickHandler(formData);
    } catch (e: any) {
      setFormError(e.message);
    }
  };

  const getUploadCTAMessageValues = () => {
    return bankData.periods.length > 1
      ? {
          p: (chunks: any) => <Text>{chunks}</Text>,
          bankName: bankData.bank_name
            ? bankData.bank_name
            : intl.formatMessage(AIR_MESSAGES.bankStatementDefaultBankName),
          bankAccountNum: bankData.bank_account_mask,
          periods: bankData.periods.map((period) => (
            <List.Item key={`${period.start}-${period.end}`}>
              <strong>
                {intl.formatMessage(COMMON_MESSAGES.periodText, {
                  dateFrom: dayjs(period.start).startOf("month").format("LL"),
                  dateTo: getEndDateForPeriodText(period.end).format("LL"),
                })}
              </strong>
            </List.Item>
          )),
        }
      : {
          p: (chunks: any) => <Text>{chunks}</Text>,
          bankName: bankData.bank_name
            ? bankData.bank_name
            : intl.formatMessage(AIR_MESSAGES.bankStatementDefaultBankName),
          bankAccountNum: bankData.bank_account_mask,
          period: bankData.periods.map((period) => (
            <strong key={`${period.start}-${period.end}`}>
              {intl.formatMessage(COMMON_MESSAGES.periodText, {
                dateFrom: dayjs(period.start).startOf("month").format("LL"),
                dateTo: getEndDateForPeriodText(period.end).format("LL"),
              })}
            </strong>
          )),
        };
  };

  const getUploadCTAMessage = () => {
    if (bankData.periods.length > 1) {
      if (bankData.bank_account_mask) {
        return (
          <FormattedMessage
            id="funding-application: air bank statement required account and periods description"
            defaultMessage="<p>Please upload statements for {bankName} account ending in {bankAccountNum} for the periods: {periods}</p>"
            values={getUploadCTAMessageValues()}
          />
        );
      } else {
        return (
          <FormattedMessage
            id="funding-application: air bank statement required account and periods description no mask"
            defaultMessage="<p>Please upload statements for your {bankName} account(s) for the periods: {periods}</p>"
            values={getUploadCTAMessageValues()}
          />
        );
      }
    } else {
      if (bankData.bank_account_mask) {
        return (
          <FormattedMessage
            id="funding-application: air bank statement required account and period description"
            defaultMessage="<p>Please upload statements for {bankName} account ending in {bankAccountNum} for the period {period}.</p>"
            values={getUploadCTAMessageValues()}
          />
        );
      } else {
        return (
          <FormattedMessage
            id="funding-application: air bank statement required account and period description no mask"
            defaultMessage="<p>Please upload statements for your {bankName} account(s) for the period {period}.</p>"
            values={getUploadCTAMessageValues()}
          />
        );
      }
    }
  };

  return (
    <Flex gap="4" direction="column">
      <Flex direction="column" gap="6">
        <Flex direction="column" gap="8">
          <Text>{intl.formatMessage(MESSAGES.bankStatementRequiredText)}</Text>
          <StatusBanner variant="info" subheading={getUploadCTAMessage()} />
        </Flex>
        <Form
          primaryAction={{
            content: intl.formatMessage(AIR_MESSAGES.bankStatementCompleteRequestButtonText),
            analyticsId: "bank-statement-upload-submit",
            disabled:
              (!fileUploadProps.uploadsState?.uploads.length ||
                fileUploadProps.uploadsState?.uploadsComplete === false ||
                !!formError ||
                unableToProvideStatements !== false) &&
              !unableToSendStatmentsReasonsValid,
          }}
          secondaryAction={{
            content: intl.formatMessage(COMMON_MESSAGES.backLabel),
            analyticsId: "bank-statement-upload-back",
            onAction: onBack,
          }}
          onSubmit={form.handleSubmit(onUploadsComplete, (e) => addError(e, { message: "form error" }))}
        >
          {formError && <StatusBanner variant="critical" subheading={formError} />}
          <UploadArea
            label={intl.formatMessage(AIR_MESSAGES.bankStatementBankStatementsText)}
            testId="bank-statements-uploader"
            onFilesAdded={fileUploadProps.onFilesAdded}
            acceptedFileTypes={getAcceptedFileTypes(bankData.periods)}
            sizeLimitMB={fileUploadProps.sizeLimitMB}
            helperText={helperText}
            multiple
          />
          {fileUploadProps.uploadsState && fileUploadProps.uploadsState.uploads.length > 0 ? (
            <FilesList uploadsState={fileUploadProps.uploadsState} onFileRemoved={fileUploadProps.onFileRemoved} />
          ) : null}
          <Text>{getStatementTypeMessage(intl, bankData.periods)}</Text>
          <ConfirmBankStatementFields
            form={form}
            airType="bank_statements_for_account"
            bankName={bankData.bank_name}
            bankAccountMasks={bankData.bank_account_mask ? [bankData.bank_account_mask] : []}
            unableToProvideStatements={unableToProvideStatements}
            setUnableToProvideStatements={setUnableToProvideStatements}
            setUnableToSendStatmentsReasonsValid={setUnableToSendStatmentsReasonsValid}
          />
        </Form>
      </Flex>
    </Flex>
  );
};
